import './startup-ai-search.css';
import './startup-ai-workspace.css';
import './startup-ai-compact-services.css';
import { createPortal } from 'react-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlass,
  X,
  ArrowRight,
  ClockCounterClockwise,
  Sparkle,
  Plus,
  Clock,
  Trash,
  CaretRight,
  ArrowsOut,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/api';
import AIActionRenderer from './AIActionRenderer';
import MarkdownMessage from './MarkdownMessage';
import { parseTimestamp } from '@/utils/datetime';

const INCUBATOR_CONTEXT = [
  { match: '/vc/dashboard', label: 'Dashboard', suggestions: ['Which founders need support today?', 'Summarize portfolio performance and reporting gaps', 'What should I prioritize across my programs?'] },
  { match: '/vc/portfolio', label: 'Portfolio', suggestions: ['Which companies need my attention?', 'Summarize portfolio growth this month', 'Which founders need a follow-up?'] },
  { match: '/vc/programs', label: 'Programs', suggestions: ['Which applicants fit my program best?', 'Summarize my application pipeline', 'Help me review my next cohort'] },
  { match: '/vc/tracker', label: 'Tracker', suggestions: ['Compare the startups I am tracking', 'Which tracked startup should I invite?', 'Summarize their growth signals'] },
  { match: '/vc/discover', label: 'Discover', suggestions: ['Find startups aligned with my thesis', 'Which sectors should I explore?', 'What should I look for in a new cohort?'] },
  { match: '/vc/reports', label: 'Reports', suggestions: ['Draft a portfolio summary for stakeholders', 'What should my next report highlight?', 'Summarize the key portfolio risks'] },
  { match: '/vc/invite', label: 'Invitations', suggestions: ['Draft a founder invitation', 'What should I include in a welcome note?', 'Help me explain monthly reporting to founders'] },
  { match: '/vc/profile', label: 'Profile', suggestions: ['Review my incubator positioning', 'Help me refine my investment thesis', 'What makes my incubator stand out?'] },
];

const PAGE_CONTEXT = [
  { match: '/startup/dashboard', label: 'Dashboard', suggestions: ['What changed in my metrics this month?', 'How can I extend my runway?', 'What should I focus on this week?'] },
  { match: '/startup/pitch-deck', label: 'Pitch Deck', suggestions: ['How can I improve my pitch?', 'What should I clarify for investors?', 'Summarize my pitch deck'] },
  { match: '/startup/programs', label: 'Programs', suggestions: ['Which programs fit my startup best?', 'Help me strengthen my next application', 'What programs am I most likely to qualify for?'] },
  { match: '/startup/benchmarks', label: 'Benchmarks', suggestions: ['How do I compare with similar startups?', 'Which benchmark should I improve first?', 'Explain the biggest gap in my metrics'] },
  { match: '/startup/update', label: 'Monthly Update', suggestions: ['Draft my monthly investor update', 'What should I highlight this month?', 'Help me explain my runway change'] },
  { match: '/startup/vault', label: 'Vault', suggestions: ['Which documents should I upload next?', 'What would an investor expect in my data room?', 'Help me organize my diligence documents'] },
  { match: '/startup/services', label: 'Services', suggestions: ['Which service would help me most right now?', 'What should I outsource at this stage?', 'Help me prioritize my operating gaps'] },
  { match: '/startup/credit', label: 'Founder Credit', suggestions: ['Which credits can I use right now?', 'What can reduce my cloud spend?', 'Show me the highest-value founder perks'] },
  { match: '/startup/marketing', label: 'Marketing', suggestions: ['What should my next growth experiment be?', 'Help me improve this campaign', 'What should I measure this week?'] },
  { match: '/startup/interactions', label: 'Interactions', suggestions: ['Summarize my recent investor interactions', 'Who should I follow up with?', 'Draft a follow-up message'] },
  { match: '/startup/profile', label: 'Profile', suggestions: ['What is missing from my profile?', 'How can I make my profile more investor-ready?', 'Review my startup positioning'] },
  { match: '/startup/equity', label: 'Equity', suggestions: ['Explain my current ownership structure', 'What should I consider before issuing more equity?', 'Summarize my cap table for an investor'] },
];

const defaultContext = { label: 'FounderBoard', suggestions: ['What should I work on next?', 'Summarize what matters on this page', 'What needs my attention today?'] };

const cleanDisplayedContent = (content) => {
  if (typeof content !== 'string') return content || '';
  const cleaned = content.trim();
  const marker = 'User question:';
  if (cleaned.startsWith('You are FounderBoard AI') && cleaned.includes(marker)) {
    return cleaned.split(marker).slice(1).join(marker).trim();
  }
  return content;
};

const questionCategory = question => {
  const text = question.toLowerCase();
  const categories = [
    [/runway|burn|cash|spend|cost|credit|perk/, 'Finance'],
    [/program|application|qualify/, 'Programs'],
    [/document|vault|data room|diligence/, 'Documents'],
    [/investor|fundrais|equity|ownership|cap table/, 'Fundraising'],
    [/revenue|mrr|growth|campaign|marketing|experiment/, 'Growth'],
    [/metric|benchmark|compare|measure|numbers/, 'Insights'],
    [/draft|message|follow.up|write|report|update|summar/, 'Communication'],
    [/profile|positioning|pitch/, 'Positioning'],
    [/priorit|focus|plan|step|action|work on|attention/, 'Strategy'],
  ];
  return categories.find(([pattern]) => pattern.test(text))?.[1] || 'Guidance';
};

const EMPTY_RECORDS = [];

const StartupAICommandBar = ({ user, startup, audience = 'startup', records = EMPTY_RECORDS, contextOverride = null }) => {
  const isIncubator = audience === 'incubator';
  const pageContexts = isIncubator ? INCUBATOR_CONTEXT : PAGE_CONTEXT;
  const chatBase = isIncubator ? '/vc/chat' : '/startup/coach';
  const location = useLocation();
  const navigate = useNavigate();
  const shellRef = useRef(null);
  const searchBarInputRef = useRef(null);
  const interactionVersion = useRef(0);
  const incubatorActionsRef = useRef(null);
  const dialogRef = useRef(null);
  const [selectedResult, setSelectedResult] = useState(0);
  const endSuggestionInteraction = () => {
    interactionVersion.current += 1;
    setDropdown(false);
    setSearchInput('');
    setInput('');
    setMode('search');
    setMessages([]);
    setSessionId('');
    setChatContext(null);
    setLoading(false);
    setTypedResponse('');
    setTypingResponse(false);
    searchBarInputRef.current?.blur();
  };
  const [dropdown, setDropdown] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [mode, setMode] = useState('search');
  const [searchInput, setSearchInput] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [chatContext, setChatContext] = useState(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [typedResponse, setTypedResponse] = useState('');
  const [typingResponse, setTypingResponse] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(true);

  const context = useMemo(
    () => contextOverride || pageContexts.find((item) => location.pathname.startsWith(item.match)) || defaultContext,
    [location.pathname, pageContexts, contextOverride]
  );

  useEffect(() => {
    setActiveSuggestion(0);
    const timer = setInterval(() => setActiveSuggestion(index => (index + 1) % context.suggestions.length), 3300);
    return () => clearInterval(timer);
  }, [context]);

  const conversationContext = open ? (chatContext || context) : context;
  const searchResults = results.slice(0, 3);
  useEffect(() => {
    if (!dropdown || mode !== 'search' || searchInput.trim().length < 2) { setResults([]); setSearching(false); setSearchError(''); return; }
    const controller = new AbortController();
    setSearching(true); setSearchError(''); setResults([]); setSelectedResult(0);
    const timer = setTimeout(async () => {
      try {
        if (isIncubator) {
          const q = searchInput.trim().toLowerCase();
          const pages = pageContexts.map(page => ({ id: page.match, title: page.label, snippet: 'Open workspace page', url: page.match, group: 'Pages' }));
          const companies = records.map(company => ({ id: company.id, title: company.startup_name || 'Company', snippet: [company.sector, company.funding_stage].filter(Boolean).join(' · '), url: `/vc/portfolio?company=${encodeURIComponent(company.id)}`, group: 'Companies' }));
          setResults([...companies, ...pages].filter(item => `${item.title} ${item.snippet}`.toLowerCase().includes(q)));
        } else {
          const response = await api.get('/startup/search', {params:{q:searchInput.trim()},signal:controller.signal});
          if (!controller.signal.aborted) setResults(response.data.results || []);
        }
      }
      catch (error) { if (!controller.signal.aborted) { setResults([]); setSearchError('Search is unavailable. Try again shortly.'); } }
      finally { if (!controller.signal.aborted) setSearching(false); }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [searchInput, dropdown, mode, isIncubator, records]);
  useEffect(() => {
    const close = event => { if (dropdown && !open && !shellRef.current?.contains(event.target)) endSuggestionInteraction(); };
    const escape = event => { if (event.key === 'Escape' && dropdown && !open) endSuggestionInteraction(); };
    document.addEventListener('pointerdown', close); document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', escape); };
  }, [dropdown, open]);
  useEffect(() => {
    if (!dropdown || open || !shellRef.current) return;
    const shell = shellRef.current;
    const previousOverflow = document.body.style.overflow;
    const stickyHost = shell.closest('.fb-ai-sticky-shell');
    const previousZIndex = stickyHost?.style.zIndex;
    const headerHost = isIncubator ? shell.closest('.inc-topline, .inc-company-overview-toolbar') : null;
    const companyOverlay = isIncubator ? shell.closest('.inc-detail-overlay') : null;
    const previousOverlayZIndex = companyOverlay?.style.zIndex;
    if (companyOverlay) companyOverlay.style.zIndex = '110';
    const previousHeaderZIndex = headerHost?.style.zIndex;
    if (headerHost) headerHost.style.zIndex = '110';
    if (stickyHost) stickyHost.style.zIndex = '101';
    document.body.style.overflow = 'hidden';
    // Disable sibling branches while preserving the bar and its ancestors.
    const disabled = [];
    let branch = shell;
    while (branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling === branch || sibling.hasAttribute('data-fb-ai-compact-backdrop') || (isIncubator && sibling.hasAttribute('data-fb-ai-navigation-trigger'))) continue;
        disabled.push([sibling, sibling.inert]);
        sibling.inert = true;
      }
      branch = branch.parentElement;
      if (branch === document.body) break;
    }
    const trapTab = event => {
      if (event.key !== 'Tab') return;
      const controls = [...shell.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href], select:not(:disabled), [tabindex="0"]')]
        .filter(element => element.getClientRects().length > 0);
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || !shell.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !shell.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', trapTab);
    return () => {
      for (const [element, previousInert] of disabled) element.inert = previousInert;
      if (stickyHost) stickyHost.style.zIndex = previousZIndex;
      if (headerHost) headerHost.style.zIndex = previousHeaderZIndex;
      if (companyOverlay) companyOverlay.style.zIndex = previousOverlayZIndex;
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', trapTab);
    };
  }, [dropdown, open]);
  const selectResult = result => {
    setDropdown(false); setSearchInput(''); navigate(result.url);
  };
  const expandChat = () => { if (searchInput.trim()) setInput(searchInput); setDropdown(false); setOpen(true); };
  const contextPicker = <div className="fb-ai-context-switch" role="group" aria-label="Conversation context"><button type="button" aria-pressed={conversationContext !== defaultContext} disabled={loading || typingResponse} onClick={() => setChatContext(context)}>{context.label}</button><button type="button" aria-pressed={conversationContext === defaultContext} disabled={loading || typingResponse} onClick={() => setChatContext(defaultContext)}>All workspace</button></div>;

  const startupName = startup?.startup_name || startup?.name || (isIncubator ? 'your incubator' : 'your startup');
  const founderName = user?.name?.split(' ')?.[0] || user?.first_name || 'Founder';


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('ai') === '1') {
      setOpen(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!open) return undefined;
    fetchSessions();
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e) => {
      if (e.key === 'Escape') closeWorkspace();
      if (e.key === 'Tab') {
        const items = [...(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href]') || [])].filter(item => item.getClientRects().length);
        const first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(focusTimer); window.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow;
      // Returning focus to the search input would reopen its compact panel.
      if (previousFocus === searchBarInputRef.current) shellRef.current?.focus();
      else previousFocus?.focus(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const fetchSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await api.get(`${chatBase}/sessions`);
      setSessions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch AI sessions', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadSession = async (sid) => {
    if (loading) return;
    try {
      const res = await api.get(`${chatBase}/history/${sid}`);
      const history = Array.isArray(res.data) ? res.data : [];
      setMessages(history.map((message) => ({
        ...message,
        content: cleanDisplayedContent(message?.content),
      })));
      const savedContext = history.find(message => message.page_context)?.page_context;
      const storedLabel = savedContext?.match(/inside the (.*?) page/); 
      setChatContext(savedContext?.includes('Across FounderBoard') ? defaultContext : pageContexts.find(item => item.label === storedLabel?.[1]) || defaultContext);
      setSessionId(sid);
      setInput('');
      setMobileHistoryOpen(false);
    } catch (err) {
      toast.error('Failed to load chat');
    }
  };

  const startNewChat = () => {
    if (loading) return;
    interactionVersion.current += 1;
    setMessages([]);
    setSessionId('');
    setTypedResponse('');
    setTypingResponse(false);
    setMode('search');
    setChatContext(context);
    setInput('');
    setMobileHistoryOpen(false);
    setSearchInput('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const submit = async (raw, options = {}) => {
    const query = (raw ?? input).trim();
    if (!query || ((loading || typingResponse) && !options.startNew)) return;
    const compact = options.responseMode === 'compact' || (!options.responseMode && !open);
    if (options.startNew) {
      interactionVersion.current += 1;
      setSessionId('');
      setTypedResponse('');
      setTypingResponse(false);
    }
    if (compact) { setOpen(false); setDropdown(true); }

    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setSearchInput('');
    setMode('ask');
    const pinnedContext = compact ? context : (chatContext || context);
    if (compact || !chatContext) setChatContext(pinnedContext);
    const requestVersion = interactionVersion.current;
    const userMessage = { role: 'user', content: query };
    setMessages((prev) => options.startNew ? [userMessage] : [...prev, userMessage]);
    const requestSessionId = options.startNew ? '' : sessionId;
    setLoading(true);

    try {
      const res = await api.post(isIncubator ? chatBase : `${chatBase}/message`, {
        message: query,
        response_mode: compact ? 'compact' : 'expanded',
        session_id: requestSessionId || undefined,
        page_context: pinnedContext === defaultContext ? `Across FounderBoard for ${startupName}. Use the authorized company records across pages.` : `The user is working inside the ${pinnedContext.label} page for ${startupName}. Prioritize guidance relevant to what they are doing on this page.`,
      });
      if (requestVersion !== interactionVersion.current) return;
      const nextSessionId = res.data?.session_id || requestSessionId;
      if (!requestSessionId && nextSessionId) setSessionId(nextSessionId);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.data?.response || 'I could not generate a response for that yet.', sources: res.data?.sources || [], follow_ups: res.data?.follow_ups || [] },
      ]);
      if (!requestSessionId) fetchSessions();
    } catch (err) {
      if (requestVersion !== interactionVersion.current) return;
      toast.error(err.response?.data?.detail || 'FounderBoard AI could not respond');
      setMessages((prev) => prev.filter((message) => message !== userMessage));
    } finally {
      if (requestVersion === interactionVersion.current) setLoading(false);
    }
  };

  const deleteSession = async (sid, event) => {
    event?.stopPropagation();
    if (!sid) return;
    try {
      await api.delete(`${chatBase}/sessions/${sid}`);
      setSessions((prev) => prev.filter((item) => item.session_id !== sid));
      if (sessionId === sid) startNewChat();
      toast.success('Chat deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete chat');
    }
  };

  const closeWorkspace = () => {
    setOpen(false);
    endSuggestionInteraction();
    setMobileHistoryOpen(false);
    const params = new URLSearchParams(location.search);
    if (params.get('ai') === '1') {
      params.delete('ai');
      const nextSearch = params.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
    }
  };

  incubatorActionsRef.current = {
    show: event => {
      const prompt = event.detail?.prompt;
      if (event.detail?.mode === 'compact' && typeof prompt === 'string' && prompt.trim()) {
        submit(prompt, { responseMode: 'compact', startNew: true });
      } else { setOpen(true); setDropdown(false); setInput(prompt || ''); }
    },
    close: closeWorkspace,
  };
  useEffect(() => {
    if (!isIncubator) return undefined;
    const show = event => incubatorActionsRef.current?.show(event);
    const close = () => incubatorActionsRef.current?.close();
    window.addEventListener('fb-incubator-ai', show);
    window.addEventListener('fb-incubator-close-ai', close);
    return () => { window.removeEventListener('fb-incubator-ai', show); window.removeEventListener('fb-incubator-close-ai', close); };
  }, [isIncubator, location.pathname, location.search]);

  const formatTime = (ts) => {
    const d = parseTimestamp(ts);
    if (!d) return '';
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const latestAnswer = [...messages].reverse().find(message => message.role === 'assistant');
  useEffect(() => {
    if (!latestAnswer) { setTypedResponse(''); setTypingResponse(false); return; }
    const fullText = latestAnswer.content || '';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setTypedResponse(fullText); setTypingResponse(false); return;
    }
    const characters = Array.from(fullText);
    const started = performance.now();
    const duration = Math.min(14000, Math.max(700, characters.length * 10));
    setTypedResponse(''); setTypingResponse(true);
    const timer = window.setInterval(() => {
      const progress = Math.min(1, (performance.now() - started) / duration);
      const count = Math.floor(progress * characters.length);
      setTypedResponse(characters.slice(0, count).join(''));
      if (progress >= 1) { window.clearInterval(timer); setTypingResponse(false); }
    }, 35);
    return () => window.clearInterval(timer);
  }, [latestAnswer]);

  const answeredQuestion = [...messages].reverse().find(message => message.role === 'user')?.content || '';
  const answerTopic = `${answeredQuestion} ${latestAnswer?.content || ''}`;
  const mentionedCompany = isIncubator ? records.find(company => company.startup_name && answerTopic.toLowerCase().includes(company.startup_name.toLowerCase()))?.startup_name : null;
  const target = mentionedCompany || 'these companies';
  const contextualFallback = /runway|burn|cash/i.test(answerTopic)
    ? [`What cash-planning steps should ${target} take next?`, `Which reported figures should I verify for ${target}?`, `Draft a founder check-in about the cash risks you identified.`]
    : /program|applicant|cohort/i.test(answerTopic)
      ? ['Explain the criteria behind this recommendation.', 'Which applicants should I review more closely based on this answer?', 'Turn this cohort recommendation into a review checklist.']
      : /growth|mrr|revenue|customer/i.test(answerTopic)
        ? [`What is driving the growth trend for ${target}?`, `Which metrics would help verify the trend you identified?`, `Suggest a practical growth review for ${target}.`]
        : ['Explain the reasoning behind this answer.', 'What information is missing from this analysis?', 'Turn this recommendation into three next steps.'];
  const followUpQuestions = [...new Set(isIncubator ? (latestAnswer?.follow_ups?.length ? latestAnswer.follow_ups : contextualFallback) : [...(latestAnswer?.follow_ups || []), ...conversationContext.suggestions])].filter(question=>typeof question==='string'&&question.trim()&&question.trim()!==answeredQuestion.trim()).slice(0,3);
  const compactMode = mode === 'ask' && messages.length ? 'answer' : searchInput.trim() ? 'search' : 'suggestions';
  const latestQuestion = [...messages].reverse().find(message => message.role === 'user');
  const hasConversation = messages.length > 0;
  const activeSession = sessions.find((session) => session.session_id === sessionId);

  return (
    <>
      {dropdown && !open && createPortal(<div className="fb-ai-compact-backdrop" data-fb-ai-compact-backdrop aria-hidden="true" onClick={endSuggestionInteraction} />, document.body)}
      <div className={`fb-search-shell ${dropdown ? 'is-compact-open' : ''}`} ref={shellRef} tabIndex={-1}>
        <div className="fb-ai-rainbow-frame fb-ai-restored-frame">
          <div className="fb-ai-restored-bar" onClick={() => searchBarInputRef.current?.focus()} data-testid="startup-ai-command-bar">
            <div className="fb-ai-orb"><Sparkle size={11} weight="fill" /></div>
            <div className="fb-ai-editable-bar">
              {!dropdown && !searchInput && <div className="fb-ai-idle-copy"><strong>Search with FounderBoard AI</strong><span key={activeSuggestion} className="fb-ai-rotating-question"> — “{context.suggestions[activeSuggestion]}”</span></div>}
              <input ref={searchBarInputRef} className="ai-command-transparent" value={searchInput} onChange={event => {setSearchInput(event.target.value); setMode('search');setDropdown(true);}} onFocus={() => setDropdown(true)} placeholder={dropdown ? (isIncubator ? 'Search portfolio companies and pages…' : 'Search programs, documents, services…') : ''} aria-label="Search FounderBoard or ask AI" aria-expanded={dropdown} aria-controls={dropdown ? "fb-search-dropdown" : undefined} onKeyDown={event => {
                if (event.nativeEvent.isComposing) return;
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setSelectedResult(index => Math.max(0, Math.min(searchResults.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))); }
                if (event.key === 'Enter' && searchInput.trim()) { event.preventDefault(); if (event.ctrlKey || event.metaKey) submit(searchInput); else if (!searching && mode === 'search' && searchResults[selectedResult]) selectResult(searchResults[selectedResult]); }
              }} />
            </div>
            <button className="fb-ai-bar-control" aria-label={dropdown ? "Dismiss AI panel" : "Open search"} onClick={event => {event.stopPropagation(); if (dropdown) endSuggestionInteraction(); else searchBarInputRef.current?.focus();}}>{dropdown ? <X size={16} /> : <MagnifyingGlass size={16} weight="bold" />}</button>
          </div>
        </div>
        {dropdown && <div className={`fb-ai-drop-stack is-${compactMode}`}><div className="fb-search-dropdown fb-ai-refined-dropdown" id="fb-search-dropdown">

          <div className="fb-ai-mode-heading"><div><span className="fb-ai-eyebrow">{compactMode === 'search' ? 'WORKSPACE SEARCH' : compactMode === 'answer' ? 'QUICK ANSWER' : (isIncubator ? 'YOUR ECOSYSTEM ASSISTANT' : 'YOUR FOUNDER COPILOT')}</span><h2>{compactMode === 'search' ? 'Find what you need' : compactMode === 'answer' ? 'A little clarity. Your next move.' : `Let’s move ${context.label.toLowerCase()} forward`}</h2></div>{compactMode !== 'search' && <span className="fb-ai-page-badge">{conversationContext.label}</span>}</div>
          {mode === 'search' && searchInput.trim() && <>
            <div className="fb-search-results">{(isIncubator ? ['Companies', 'Pages'] : ['Programs','Documents','Services','Records']).map(group => {const items = searchResults.filter(item => item.group === group); return items.length ? <div key={group}><h3>{group}</h3>{items.map(item => <button className={searchResults.indexOf(item) === selectedResult ? 'is-selected' : ''} key={`${group}-${item.id}`} onMouseEnter={() => setSelectedResult(searchResults.indexOf(item))} onClick={() => selectResult(item)}><span><strong>{item.title}</strong><small>{item.snippet}</small></span><CaretRight size={15} /></button>)}</div> : null;})}
            {searching && <p role="status">Searching your workspace…</p>}{searchError && <p role="alert">{searchError}</p>}{!searching && !searchError && !searchResults.length && <p>{searchInput.trim().length < 2 ? 'Type at least two characters to search.' : 'Nothing found yet. Try another keyword or ask AI below.'}</p>}</div>
            <button className="fb-search-ask-query" onClick={() => submit(searchInput)}><Sparkle size={16} />Ask AI: {searchInput}<ArrowRight size={14} /></button>
          </>}
          {mode === 'ask' && messages.length > 0 ? <div className="fb-ai-answer-panel">
            <div className="fb-ai-current-question"><span className="fb-ai-question-label">Your question</span><p>{latestQuestion?.content}</p></div>
            <div className="fb-search-compact-messages">{(!loading && latestAnswer ? [latestAnswer] : []).map((message,index) => <div key={index} className={`fb-search-message ${message.role}`}><small>{message.role === 'user' ? 'You' : 'FounderBoard AI'}</small>{message.role === 'assistant' ? <><div className={typingResponse ? 'fb-ai-typing-text' : ''}>{typingResponse ? <MarkdownMessage content={typedResponse} /> : <AIActionRenderer content={message.content} />}</div></> : <p>{message.content}</p>}{!typingResponse && message.sources?.length > 0 && <div className="fb-search-sources"><small>Context records</small>{message.sources.map(source => <button key={source.url} onClick={() => selectResult(source)}>{source.title}</button>)}</div>}</div>)}{loading && <div className="fb-ai-thinking" role="status"><Sparkle size={16} />Thinking through your question<span /><span /><span /></div>}</div>
            {!loading && !typingResponse && <div className="fb-ai-question-grid fb-ai-followups">{followUpQuestions.map((question,index) => <button key={question} disabled={loading} className="fb-ai-question" onClick={() => submit(question)}><span className="fb-ai-category-pill">{questionCategory(question)}</span><span className="fb-ai-prompt-copy">{question}</span></button>)}</div>}
          </div> : !searchInput.trim() && <div className="fb-ai-question-grid">{context.suggestions.map((question,index) => <button key={question} className="fb-ai-question" onClick={() => submit(question)}><span className="fb-ai-category-pill">{questionCategory(question)}</span><span className="fb-ai-prompt-copy">{question}</span></button>)}</div>}
        </div>
          <div className="fb-ai-expand-footer"><button className="fb-ai-expand-v2" onClick={expandChat} aria-label="Expand chat"><ArrowsOut size={19} weight="bold" /><span>Expand Chat <small className="fb-ai-expand-hint">{hasConversation ? 'Continue this conversation' : 'Room to think, plan, and create'}</small></span></button></div>
        </div>}
      </div>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-2 sm:p-3 fb-ai-backdrop-enter"
          style={{ backgroundColor: 'rgba(3, 8, 14, 0.84)', backdropFilter: 'blur(5px)' }}
          onMouseDown={closeWorkspace}
          data-testid="startup-ai-workspace-backdrop"
        >
          <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="FounderBoard AI conversation"
            className={`${isIncubator ? 'incubator-ai-theme ' : ''}fb-ai-expanded-theme fb-ai-workspace-v2 w-full max-w-[1480px] h-[calc(100dvh-1rem)] sm:h-[calc(100dvh-1.5rem)] overflow-hidden rounded-[26px] border shadow-[0_30px_90px_rgba(2,8,23,0.34)] fb-ai-workspace-enter`}
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={`fb-ai-workspace-layout ${historyVisible ? 'with-history' : ''}`}>
              <aside
                className={`fb-ai-history-panel ${historyVisible ? 'hidden md:flex' : 'hidden'} min-h-0 flex-col border-r`}
                style={{ backgroundColor: 'color-mix(in srgb, var(--background) 86%, transparent)', borderColor: 'var(--border)' }}
              >
                <div className="px-4 pt-4 pb-3">
                  <div className="flex items-center gap-2.5 px-1">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#0f766e] via-[#0d9488] to-[#2dd4bf] flex items-center justify-center shadow-[0_8px_20px_rgba(16,185,129,0.16)]">
                      <Sparkle size={16} weight="fill" className="text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-bold text-text-primary">FounderBoard AI</div>
                      <div className="text-[10px] text-text-muted truncate">Your thinking space</div>
                    </div>
                  </div>
                </div>

                <div className="px-3 pb-3">
                  <button
                    onClick={startNewChat}
                    className="w-full h-10 rounded-xl bg-[#10b981] text-white flex items-center justify-center gap-2 text-[12px] font-semibold hover:bg-[#0d9488] hover:-translate-y-0.5 active:translate-y-0 transition-all shadow-[0_8px_20px_rgba(16,185,129,0.18)]"
                  >
                    <Plus size={14} weight="bold" />
                    New chat
                  </button>
                </div>

                <div className="mx-3 rounded-xl border px-3 py-3" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                  <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-[#0d9488]">Active context</div>
                  <div className="mt-1.5 text-[12px] font-semibold text-text-primary truncate">{conversationContext.label}</div>
                  <div className="mt-0.5 text-[10px] text-text-muted truncate">{startupName}</div>
                </div>

                <div className="px-4 pt-4 pb-2 flex items-center justify-between">
                  <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-text-muted">History</div>
                  <ClockCounterClockwise size={13} className="text-text-muted" />
                </div>

                <div className="flex-1 overflow-y-auto px-2.5 pb-3 space-y-1.5">
                  {sessionsLoading ? (
                    <div className="px-2.5 py-3 text-[11px] text-text-muted">Loading chats…</div>
                  ) : sessions.length ? sessions.map((session) => (
                    <div key={session.session_id} className="group flex items-stretch gap-1.5 fb-ai-session-row">
                      <button
                        onClick={() => loadSession(session.session_id)}
                        aria-current={sessionId === session.session_id ? 'true' : undefined}
                        className={`fb-ai-history-item min-w-0 flex-1 text-left rounded-xl px-3 py-2.5 border transition-all duration-200 ${
                          sessionId === session.session_id
                            ? 'border-[#10b981]/35 bg-[#10b981]/10 text-text-primary shadow-[0_4px_14px_rgba(16,185,129,0.08)]'
                            : 'border-transparent text-text-secondary hover:bg-surface hover:text-text-primary'
                        }`}
                      >
                        <div className="truncate text-[11px] font-semibold">{session.title || 'FounderBoard AI chat'}</div>
                        <div className="mt-1.5 flex items-center gap-1 text-[9px] text-text-muted">
                          <Clock size={10} />
                          {formatTime(session.last_timestamp)}
                          {session.message_count != null && <span className="ml-auto">{session.message_count} msgs</span>}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(event) => deleteSession(session.session_id, event)}
                        className="w-8 shrink-0 rounded-xl border border-red-500/25 bg-red-500/5 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white hover:border-red-500 transition-all duration-200"
                        aria-label={`Delete ${session.title || 'chat'}`}
                        title="Delete chat"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  )) : (
                    <div className="px-2.5 py-3 text-[11px] text-text-muted">Your chats will appear here.</div>
                  )}
                </div>
              </aside>

              <section className="relative min-w-0 min-h-0 flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--card)' }}>
                <header className="shrink-0 border-b px-3 sm:px-5 py-3.5" style={{ borderColor: 'var(--border)' }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <button type="button" className="fb-ai-history-toggle hidden md:flex" aria-label={historyVisible ? 'Hide chat history' : 'Show chat history'} aria-expanded={historyVisible} onClick={() => setHistoryVisible(value => !value)}><ClockCounterClockwise size={18} /></button>
                      <button
                        type="button"
                        onClick={() => setMobileHistoryOpen((value) => !value)}
                        className="md:hidden w-9 h-9 rounded-xl border flex items-center justify-center text-text-secondary"
                        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}
                        aria-label="Open chat history"
                      >
                        <ClockCounterClockwise size={15} weight="bold" />
                      </button>
                      <div className="min-w-0">
                        <div className="fb-ai-workspace-title">{activeSession?.title || 'FounderBoard AI'}</div>
                        <div className="fb-ai-workspace-subtitle">Your thinking space · {startupName}</div>
                      </div>
                    </div>
                    <div className="fb-ai-header-actions flex items-center gap-2 shrink-0">
                      {contextPicker}
                      <button
                        type="button"
                        onClick={startNewChat}
                        className="md:hidden h-9 px-3 rounded-xl bg-[#10b981] text-white flex items-center gap-1.5 text-[11px] font-semibold"
                      >
                        <Plus size={13} weight="bold" /> New
                      </button>
                      <button
                        onClick={closeWorkspace}
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-text-muted hover:bg-background hover:text-text-primary transition-all"
                        aria-label="Close FounderBoard AI"
                      >
                        <X size={16} weight="bold" />
                      </button>
                    </div>
                  </div>
                </header>

                {mobileHistoryOpen && (
                  <div className="md:hidden absolute left-2 right-2 top-[67px] z-30 max-h-[58dvh] overflow-y-auto rounded-2xl border p-2 shadow-[0_24px_60px_rgba(15,23,42,0.28)] fb-ai-history-enter" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
                    <div className="flex items-center justify-between px-2 pb-1.5">
                      <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-text-muted">Recent chats</div>
                      <button type="button" onClick={() => setMobileHistoryOpen(false)} className="w-7 h-7 rounded-lg flex items-center justify-center text-text-muted"><X size={13} /></button>
                    </div>
                    {sessionsLoading ? (
                      <div className="px-3 py-4 text-xs text-text-muted">Loading chats…</div>
                    ) : sessions.length ? sessions.map((session) => (
                      <div key={session.session_id} className="flex items-stretch gap-2 py-1">
                        <button
                          type="button"
                          onClick={() => loadSession(session.session_id)}
                        aria-current={sessionId === session.session_id ? 'true' : undefined}
                          className={`fb-ai-history-item min-w-0 flex-1 rounded-xl border px-3 py-2.5 text-left ${sessionId === session.session_id ? 'border-[#10b981]/35 bg-[#10b981]/10' : 'border-transparent hover:bg-surface'}`}
                        >
                          <div className="truncate text-xs font-semibold text-text-primary">{session.title || 'FounderBoard AI chat'}</div>
                          <div className="mt-1 text-[10px] text-text-muted">{formatTime(session.last_timestamp)}</div>
                        </button>
                        <button
                          type="button"
                          onClick={(event) => deleteSession(session.session_id, event)}
                          className="w-10 shrink-0 rounded-xl border border-red-500/30 bg-red-500/5 text-red-500 flex items-center justify-center active:scale-95 transition-all"
                          aria-label={`Delete ${session.title || 'chat'}`}
                        >
                          <Trash size={15} weight="bold" />
                        </button>
                      </div>
                    )) : (
                      <div className="px-3 py-4 text-xs text-text-muted">No saved chats yet.</div>
                    )}
                  </div>
                )}

                <div className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-6 fb-ai-chat-scroll">
                  {!hasConversation && !loading ? (
                    <div className="max-w-[760px] mx-auto min-h-full flex flex-col justify-center py-4 sm:py-8">
                      <div className="fb-ai-services-welcome max-w-2xl">
                        <div className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] font-semibold text-text-secondary" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}>
                          <Sparkle size={11} weight="fill" className="text-[#10b981]" />
                          Working with {conversationContext.label} context
                        </div>
                        <h2 className="mt-4 text-[28px] sm:text-[36px] leading-[1.08] tracking-[-0.035em] font-heading font-bold text-text-primary">
                          What’s your next move, {founderName}?
                        </h2>
                        <p className="mt-3 max-w-xl text-[13px] sm:text-[14px] leading-6 text-text-secondary">
                          Find clarity in your numbers, shape an idea, or plan what comes next.
                        </p>
                      </div>

                      <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                        {conversationContext.suggestions.map((suggestion, index) => (
                          <button
                            key={suggestion}
                            onClick={() => submit(suggestion)}
                            className="fb-ai-suggestion-card group text-left rounded-2xl border p-4 min-h-[118px] flex flex-col justify-between transition-all duration-200 hover:-translate-y-1 hover:border-[#10b981]/35 hover:shadow-[0_12px_28px_rgba(15,23,42,0.08)]"
                            style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }}
                          >
                            <div className="fb-ai-services-prompt-icon"><Sparkle size={19} weight="duotone" /></div>
                            <div className="mt-4 flex items-end justify-between gap-4">
                              <span className="text-[12px] sm:text-[13px] font-semibold leading-5 text-text-primary">{suggestion}</span>
                              <span className="w-7 h-7 rounded-full border flex items-center justify-center text-text-muted group-hover:text-[#10b981] group-hover:border-[#10b981]/30 transition-colors shrink-0" style={{ borderColor: 'var(--border)' }}>
                                <CaretRight size={12} weight="bold" />
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-[760px] mx-auto space-y-6 pb-2">
                      {messages.map((msg, index) => (
                        <div key={`${msg.role}-${index}`} className={`fb-ai-expanded-message ${msg.role} flex gap-3 fb-ai-message-enter ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          {msg.role === 'assistant' && (
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#10b981] to-[#0d9488] flex items-center justify-center shrink-0 mt-0.5 shadow-[0_6px_16px_rgba(16,185,129,0.16)]">
                              <Sparkle size={15} className="text-white" weight="fill" />
                            </div>
                          )}
                          <div
                            className={`px-4 py-3.5 rounded-2xl max-w-[88%] sm:max-w-[82%] text-sm leading-6 ${
                              msg.role === 'user'
                                ? 'bg-[#10b981] text-white rounded-br-md shadow-[0_8px_22px_rgba(16,185,129,0.16)]'
                                : 'border rounded-bl-md text-text-primary shadow-[0_6px_20px_rgba(15,23,42,0.04)]'
                            }`}
                            style={msg.role === 'assistant' ? { backgroundColor: 'var(--background)', borderColor: 'var(--border)' } : undefined}
                          >
                            {msg.role === 'assistant' ? <>{msg === latestAnswer && typingResponse ? <div className="fb-ai-typing-text"><MarkdownMessage content={typedResponse} /></div> : <AIActionRenderer content={msg.content} />}{msg.sources?.length > 0 && <div className="fb-search-sources"><small>Context records</small>{msg.sources.map(source => <button key={source.url} onClick={() => {closeWorkspace();navigate(source.url);}}>{source.title}</button>)}</div>}</> : <p className="whitespace-pre-wrap">{msg.content}</p>}
                          </div>
                        </div>
                      ))}
                      {loading && (
                        <div className="flex gap-3 fb-ai-message-enter">
                          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#10b981] to-[#0d9488] flex items-center justify-center shrink-0">
                            <Sparkle size={15} className="text-white" weight="fill" />
                          </div>
                          <div className="px-4 py-3.5 border rounded-2xl rounded-bl-md" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }}>
                            <div className="flex items-center gap-1.5">
                              <div className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce" />
                              <div className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                              <div className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                            </div>
                          </div>
                        </div>
                      )}
                      {latestAnswer && <div className="fb-ai-detail-action"><button disabled={loading || typingResponse} onClick={() => submit('Explain your previous answer in detail, including the reasoning and relevant numbers.')}><Sparkle size={14} />Explain in detail</button></div>}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                <div className="shrink-0 px-3 sm:px-6 lg:px-8 pb-3 sm:pb-5 pt-2 fb-ai-composer-shell">
                  <div className="max-w-[760px] mx-auto">
                    <div
                      className="rounded-[18px] border p-2 shadow-[0_12px_28px_rgba(15,23,42,0.08)] transition-shadow focus-within:shadow-[0_16px_36px_rgba(16,185,129,0.11)] focus-within:border-[#10b981]/35"
                      style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }}
                    >
                      <div className="fb-ai-composer-row flex items-center gap-2">
                        <textarea
                          rows={2}
                          aria-label="Message FounderBoard AI"
                          ref={inputRef}
                          value={input}
                          onChange={(e) => {setInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = `${Math.min(e.target.scrollHeight, 180)}px`;}}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); } }}
                          placeholder={`Ask about ${conversationContext.label.toLowerCase()}, your company, or what to do next…`}
                          className="ai-command-transparent resize-none h-auto flex-1 min-w-0 bg-transparent px-2 outline-none text-[13px] sm:text-sm text-text-primary placeholder:text-text-muted"
                          style={{ backgroundColor: 'transparent' }}
                          disabled={loading || typingResponse}
                          data-testid="startup-ai-overlay-input"
                        />
                        <button
                          onClick={() => submit()}
                          disabled={!input.trim() || loading || typingResponse}
                          className="w-10 h-10 rounded-xl bg-[#10b981] text-white flex items-center justify-center disabled:opacity-35 disabled:cursor-not-allowed hover:bg-[#0d9488] active:scale-95 transition-all shrink-0 shadow-[0_6px_16px_rgba(16,185,129,0.18)]"
                          aria-label="Send"
                          title="Send message"
                        >
                          <ArrowRight size={19} weight="bold" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 text-center text-[9px] sm:text-[10px] text-text-muted">
                      Using {conversationContext.label} context · Enter to send · Shift + Enter for a new line
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      , document.body)}
    </>
  );
};

export default StartupAICommandBar;
